/**
 * Full Terms of Service — informational template; not individualized legal advice.
 */
const TERMS_SECTIONS = {
  en: [
    {
      id: "terms-accept",
      title: "1. Acceptance of terms",
      paragraphs: [
        'By accessing or using AfterMe (the "Service"), you agree to these Terms of Service ("Terms"). If you do not agree, do not use the Service. We may update these Terms from time to time; the "Effective" date above will change, and continued use after changes constitutes acceptance of the revised Terms.',
      ],
    },
    {
      id: "terms-eligibility",
      title: "2. Eligibility and accounts",
      paragraphs: [
        "You must be at least the age of majority in your province or territory of residence to create an account. You are responsible for providing accurate registration information and for maintaining the confidentiality of your credentials. You are responsible for all activity under your account unless you notify us promptly of unauthorized use using the contact information below.",
      ],
    },
    {
      id: "terms-service",
      title: "3. Description of the Service",
      paragraphs: [
        'AfterMe provides tools to help you organize digital legacy content (such as journals, letters, media, and family-related metadata), manage access intentions, and support workflows around verification and release as described in the product. Features may change, be added, or be discontinued. The Service is provided on an "as is" and "as available" basis.',
      ],
    },
    {
      id: "terms-content",
      title: "4. Your content and licence to us",
      paragraphs: [
        'You retain ownership of content you submit ("User Content"). To operate the Service, you grant AfterMe a non-exclusive, worldwide, royalty-free licence to host, store, reproduce, display, and transmit User Content solely to provide, secure, improve, and comply with law regarding the Service. You represent that you have the rights needed to grant this licence and that your User Content does not violate third-party rights or applicable law.',
      ],
    },
    {
      id: "terms-acceptable",
      title: "5. Acceptable use",
      paragraphs: [
        "You agree not to:",
        "We may investigate violations and suspend or terminate access where we reasonably believe it is necessary to protect users, the Service, or to comply with law.",
      ],
      list: [
        "Use the Service for unlawful, harassing, defamatory, fraudulent, or harmful purposes.",
        "Attempt to gain unauthorized access to systems, data, or accounts.",
        "Upload malware or interfere with the integrity or performance of the Service.",
        "Misrepresent your identity or impersonate others.",
        "Scrape, crawl, or automate access in a way that burdens or disrupts the Service.",
      ],
    },
    {
      id: "terms-family",
      title: "6. Family, recipients, and release",
      paragraphs: [
        "Tools that let you designate family members, recipients, or release paths are provided for your convenience. You are responsible for the accuracy of designations and for understanding that operational and legal outcomes may depend on circumstances outside our control. AfterMe does not provide legal, medical, tax, or estate-planning advice.",
      ],
    },
    {
      id: "terms-ip",
      title: "7. Our intellectual property",
      paragraphs: [
        "The Service, including software, branding, text, layout, and documentation (excluding your User Content), is owned by AfterMe or its licensors and is protected by intellectual property laws. Except for the limited rights expressly granted here, no rights are transferred to you.",
      ],
    },
    {
      id: "terms-third",
      title: "8. Third-party services",
      paragraphs: [
        "The Service may integrate with third parties (for example, identity or hosting providers). Those services are governed by their own terms and privacy policies. We are not responsible for third-party services we do not control.",
      ],
    },
    {
      id: "terms-privacy",
      title: "9. Privacy",
      paragraphs: [
        "Our collection and use of personal information is described in our privacy materials published on the site (including summaries in the site footer). By using the Service, you acknowledge those practices.",
      ],
    },
    {
      id: "terms-disclaimer",
      title: "10. Disclaimers",
      paragraphs: [
        "To the fullest extent permitted by applicable law, the Service is provided without warranties of any kind, whether express or implied, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or free of harmful components.",
      ],
    },
    {
      id: "terms-liability",
      title: "11. Limitation of liability",
      paragraphs: [
        "To the fullest extent permitted by applicable law, AfterMe and its directors, officers, employees, and affiliates will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, data, goodwill, or other intangible losses, arising from your use of or inability to use the Service. Our aggregate liability for claims arising out of these Terms or the Service will not exceed the greater of (a) the amounts you paid us for the Service in the twelve (12) months before the claim or (b) one hundred Canadian dollars (CAD $100), if you have not paid fees.",
        "Some jurisdictions do not allow certain limitations; in those cases, our liability is limited to the maximum extent permitted by law.",
      ],
    },
    {
      id: "terms-indemnity",
      title: "12. Indemnity",
      paragraphs: [
        "You will defend, indemnify, and hold harmless AfterMe from claims, damages, losses, and expenses (including reasonable legal fees) arising from your User Content, your use of the Service, or your violation of these Terms or applicable law, except to the extent caused by our gross negligence or wilful misconduct.",
      ],
    },
    {
      id: "terms-suspension",
      title: "13. Suspension and termination",
      paragraphs: [
        "You may stop using the Service at any time. We may suspend or terminate access if you breach these Terms, if we are required to do so by law, or if we discontinue the Service. Provisions that by their nature should survive (including licences granted to the extent needed to wind down storage, disclaimers, limitations, and indemnity) will survive termination.",
      ],
    },
    {
      id: "terms-law",
      title: "14. Governing law and disputes",
      paragraphs: [
        "These Terms are governed by the laws of Canada and the Province of British Columbia, without regard to conflict-of-law principles. Subject to any non-waivable rights you have under consumer protection law in your province or territory of residence, you agree that the courts located in British Columbia have exclusive jurisdiction over disputes arising out of or relating to these Terms or the Service.",
      ],
    },
    {
      id: "terms-contact",
      title: "15. Contact",
      paragraphs: ["Questions about these Terms:"],
      contactLabel: "support@afterme.ca",
    },
  ],
  fr: [
    {
      id: "terms-accept",
      title: "1. Acceptation des conditions",
      paragraphs: [
        "En accedant a AfterMe ou en utilisant le service (le « Service »), vous acceptez les presentes conditions d'utilisation (les « Conditions »). Si vous n'etes pas d'accord, n'utilisez pas le Service. Nous pouvons modifier ces Conditions de temps a autre; la date « En vigueur le » ci-dessus changera, et votre utilisation continue apres une modification constitue votre acceptation des Conditions revisees.",
      ],
    },
    {
      id: "terms-eligibility",
      title: "2. Admissibilite et comptes",
      paragraphs: [
        "Vous devez avoir atteint l'age de la majorite dans votre province ou territoire de residence pour creer un compte. Vous etes responsable de l'exactitude des renseignements d'inscription que vous fournissez et de la confidentialite de vos identifiants. Vous etes responsable de toute activite effectuee sur votre compte, sauf si vous nous informez rapidement d'une utilisation non autorisee au moyen des coordonnees ci-dessous.",
      ],
    },
    {
      id: "terms-service",
      title: "3. Description du Service",
      paragraphs: [
        "AfterMe fournit des outils pour vous aider a organiser du contenu lie a votre heritage numerique (comme des journaux, lettres, medias et metadonnees familiales), a gerer les intentions d'acces et a soutenir les workflows de verification et de diffusion decrits dans le produit. Les fonctionnalites peuvent changer, etre ajoutees ou etre retirees. Le Service est fourni « tel quel » et « selon disponibilite ».",
      ],
    },
    {
      id: "terms-content",
      title: "4. Votre contenu et la licence que vous nous accordez",
      paragraphs: [
        "Vous conservez la propriete du contenu que vous soumettez (le « Contenu utilisateur »). Pour exploiter le Service, vous accordez a AfterMe une licence non exclusive, mondiale et libre de redevances lui permettant d'heberger, stocker, reproduire, afficher et transmettre votre Contenu utilisateur uniquement afin de fournir, securiser, ameliorer le Service et se conformer a la loi. Vous declarez disposer des droits necessaires pour accorder cette licence et que votre Contenu utilisateur ne viole pas les droits de tiers ni la loi applicable.",
      ],
    },
    {
      id: "terms-acceptable",
      title: "5. Utilisation acceptable",
      paragraphs: [
        "Vous acceptez de ne pas :",
        "Nous pouvons enqueter sur les violations et suspendre ou resilier l'acces lorsque nous estimons raisonnablement que cela est necessaire pour proteger les utilisateurs, le Service ou pour respecter la loi.",
      ],
      list: [
        "Utiliser le Service a des fins illegales, harcelantes, diffamatoires, frauduleuses ou nuisibles.",
        "Tenter d'obtenir un acces non autorise a des systemes, des donnees ou des comptes.",
        "Televerser des logiciels malveillants ou nuire a l'integrite ou a la performance du Service.",
        "Faussement representer votre identite ou usurper l'identite d'autrui.",
        "Explorer, indexer ou automatiser l'acces d'une maniere qui surcharge ou perturbe le Service.",
      ],
    },
    {
      id: "terms-family",
      title: "6. Famille, destinataires et diffusion",
      paragraphs: [
        "Les outils qui vous permettent de designer des membres de la famille, des destinataires ou des parcours de diffusion sont fournis pour votre commodite. Vous etes responsable de l'exactitude de ces designations et du fait de comprendre que les consequences operationnelles et juridiques peuvent dependre de circonstances hors de notre controle. AfterMe ne fournit pas de conseils juridiques, medicaux, fiscaux ni de planification successorale.",
      ],
    },
    {
      id: "terms-ip",
      title: "7. Notre propriete intellectuelle",
      paragraphs: [
        "Le Service, y compris le logiciel, l'image de marque, les textes, la mise en page et la documentation (a l'exclusion de votre Contenu utilisateur), appartient a AfterMe ou a ses concédants et est protege par les lois sur la propriete intellectuelle. Sauf pour les droits limites expressement accordes dans les presentes, aucun droit ne vous est transfere.",
      ],
    },
    {
      id: "terms-third",
      title: "8. Services de tiers",
      paragraphs: [
        "Le Service peut s'integrer a des services tiers (par exemple des fournisseurs d'identite ou d'hebergement). Ces services sont regis par leurs propres conditions d'utilisation et politiques de confidentialite. Nous ne sommes pas responsables des services tiers que nous ne controlons pas.",
      ],
    },
    {
      id: "terms-privacy",
      title: "9. Confidentialite",
      paragraphs: [
        "La collecte et l'utilisation des renseignements personnels sont decrites dans nos documents de confidentialite publies sur le site (y compris les resumes presentes dans le pied de page). En utilisant le Service, vous reconnaissez ces pratiques.",
      ],
    },
    {
      id: "terms-disclaimer",
      title: "10. Exclusions de garantie",
      paragraphs: [
        "Dans toute la mesure permise par la loi applicable, le Service est fourni sans garantie d'aucune sorte, expresse ou implicite, y compris toute garantie implicite de qualite marchande, d'adequation a un usage particulier et d'absence de contrefacon. Nous ne garantissons pas que le Service sera ininterrompu, exempt d'erreurs ou depourvu d'elements nuisibles.",
      ],
    },
    {
      id: "terms-liability",
      title: "11. Limitation de responsabilite",
      paragraphs: [
        "Dans toute la mesure permise par la loi applicable, AfterMe ainsi que ses administrateurs, dirigeants, employes et societes affiliees ne pourront etre tenus responsables de dommages indirects, accessoires, particuliers, consecutifs ou punitifs, ni de pertes de profits, de donnees, de clientele ou d'autres pertes immaterielles, decoulant de votre utilisation ou de votre impossibilite d'utiliser le Service. Notre responsabilite totale pour les reclamations decoulant des presentes Conditions ou du Service n'excedera pas le montant le plus eleve entre (a) les sommes que vous nous avez versees pour le Service au cours des douze (12) mois precedant la reclamation et (b) cent dollars canadiens (100 $ CA), si vous n'avez paye aucuns frais.",
        "Certaines juridictions n'autorisent pas certaines limitations; dans ce cas, notre responsabilite est limitee dans toute la mesure permise par la loi.",
      ],
    },
    {
      id: "terms-indemnity",
      title: "12. Indemnisation",
      paragraphs: [
        "Vous indemniserez et degagerez AfterMe de toute responsabilite a l'egard des reclamations, dommages, pertes et depenses (y compris les frais juridiques raisonnables) decoulant de votre Contenu utilisateur, de votre utilisation du Service ou de votre violation des presentes Conditions ou de la loi applicable, sauf dans la mesure ou ces consequences sont causees par notre negligence grave ou notre faute intentionnelle.",
      ],
    },
    {
      id: "terms-suspension",
      title: "13. Suspension et resiliation",
      paragraphs: [
        "Vous pouvez cesser d'utiliser le Service en tout temps. Nous pouvons suspendre ou resilier l'acces si vous enfreignez les presentes Conditions, si la loi nous y oblige ou si nous mettons fin au Service. Les dispositions qui, par leur nature, doivent survivre a la resiliation (y compris les licences accordees dans la mesure necessaire a la fermeture du stockage, les exclusions de garantie, les limitations et l'indemnisation) survivront a la resiliation.",
      ],
    },
    {
      id: "terms-law",
      title: "14. Droit applicable et differends",
      paragraphs: [
        "Les presentes Conditions sont regies par les lois du Canada et de la province de la Colombie-Britannique, sans egard aux principes de conflits de lois. Sous reserve des droits non derogatoires que vous pouvez avoir en vertu des lois sur la protection du consommateur de votre province ou territoire de residence, vous acceptez que les tribunaux situes en Colombie-Britannique aient competence exclusive pour trancher tout differend decoulant des presentes Conditions ou du Service, ou s'y rapportant.",
      ],
    },
    {
      id: "terms-contact",
      title: "15. Coordonnees",
      paragraphs: ["Questions au sujet des presentes Conditions :"],
      contactLabel: "support@afterme.ca",
    },
  ],
};

export function TermsOfServicePage({ copy, locale = "en" }) {
  const isFrench = locale === "fr";
  const sections = TERMS_SECTIONS[isFrench ? "fr" : "en"];
  const effective = new Intl.DateTimeFormat(isFrench ? "fr-CA" : "en-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date());

  return (
    <article className="terms-page" lang={isFrench ? "fr-CA" : "en-CA"}>
      <header className="terms-page-intro">
        <p className="terms-page-kicker">{copy.legalKicker}</p>
        <h1 id="terms-top" className="terms-page-title">
          {copy.legalTitle}
        </h1>
        <p className="terms-page-meta">
          <strong>AfterMe.ca</strong> · {copy.legalMetaPrefix} {effective}
        </p>
        <p className="terms-page-disclaimer-callout">{copy.legalDisclaimer}</p>
        {copy.translationNote ? <p className="terms-page-meta">{copy.translationNote}</p> : null}
      </header>

      <div className="terms-page-body">
        {sections.map((section) => (
          <section key={section.id} className="terms-section" aria-labelledby={section.id}>
            <h2 id={section.id}>{section.title}</h2>
            {section.paragraphs.map((paragraph, index) => {
              const isListIntro = section.list && index === 0;
              const isListOutro = section.list && index === section.paragraphs.length - 1;

              if (section.contactLabel && index === 0) {
                return (
                  <p key={paragraph}>
                    {paragraph}{" "}
                    <a href="mailto:support@afterme.ca" className="terms-inline-link">
                      {section.contactLabel}
                    </a>
                    .
                  </p>
                );
              }

              if (isListIntro) {
                return <p key={paragraph}>{paragraph}</p>;
              }

              if (isListOutro && section.paragraphs.length > 1) {
                return <p key={paragraph}>{paragraph}</p>;
              }

              return <p key={paragraph}>{paragraph}</p>;
            })}
            {section.list ? (
              <ul>
                {section.list.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
      </div>
    </article>
  );
}
